import React, { useState, useEffect } from "react";
import { useTheme } from "../../context/ThemeContext";

interface DishForm3Props {
  onNext: (data: DishForm3Data) => void;
  onPrevious: () => void;
  onChange?: (data: Partial<DishForm3Data>) => void;
  initialData?: DishForm3Data;
}

export interface DishForm3Data {
  pricingModel: "Fixed Price" | "Variant Based";
  basePrice: string;
  comparePrice: string;
  gstInclusive: boolean;
  gstRate: string;
  packagingCharge: string;
}

const DishForm3: React.FC<DishForm3Props> = ({
  onNext,
  onPrevious,
  onChange,
  initialData,
}) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState<DishForm3Data>({
    pricingModel: initialData?.pricingModel || "Fixed Price",
    basePrice: initialData?.basePrice || "",
    comparePrice: initialData?.comparePrice || "",
    gstInclusive: initialData?.gstInclusive || false,
    gstRate: initialData?.gstRate || "",
    packagingCharge: initialData?.packagingCharge || "",
  });

  const gstRateOptions = ["0%", "5%", "12%", "18%", "28%"];
  const [error, setError] = useState("");

  // Update form data when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData) {
      setFormData({
        pricingModel: initialData.pricingModel || "Fixed Price",
        basePrice: initialData.basePrice || "",
        comparePrice: initialData.comparePrice || "",
        gstInclusive: initialData.gstInclusive || false,
        gstRate: initialData.gstRate || "",
        packagingCharge: initialData.packagingCharge || "",
      });
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    const updatedData = { [name]: value };
    setFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));
    setError("");
    // Call onChange immediately to update parent state
    if (onChange) {
      onChange(updatedData);
    }
  };

  const handleToggle = () => {
    const newGstInclusive = !formData.gstInclusive;
    setFormData((prev) => ({
      ...prev,
      gstInclusive: newGstInclusive,
    }));
    // Call onChange immediately to update parent state
    if (onChange) {
      onChange({ gstInclusive: newGstInclusive });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.basePrice.trim()) {
      setError("Base price is required");
      return;
    }
    if (parseFloat(formData.basePrice) <= 0) {
      setError("Base price must be greater than 0");
      return;
    }
    if (parseFloat(formData.comparePrice) <= 0) {
      setError("Compare price must be greater than 0");
      return;
    }
    if (parseFloat(formData.comparePrice) <= parseFloat(formData.basePrice)) {
      setError("Compare price must be greater than Base price");
      return;
    }
    if (!formData.gstRate) {
      setError("Please select a GST rate");
      return;
    }

    onNext(formData);
  };

  return (
    <div className="px-3 sm:px-4 md:px-6">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Pricing Model */}
        <div>
          <label className={`block font-Urbanist text-lg sm:text-[23px] font-semibold mb-3 leading-[140%] ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
            Pricing Model
          </label>
          <div className="flex gap-2 sm:gap-3 w-full">
            <button
              type="button"
              onClick={() => {
                setFormData((prev) => ({ ...prev, pricingModel: "Fixed Price" }));
                if (onChange) {
                  onChange({ pricingModel: "Fixed Price" });
                }
              }}
              className={`w-1/2 px-6 py-2.5 rounded-lg font-Urbanist text-[14px] font-medium transition-colors ${
                formData.pricingModel === "Fixed Price"
                  ? "bg-[#FF8A00] text-white"
                  : isDarkMode
                    ? "text-gray-300 border-gray-600 border-2 bg-gray-700"
                    : "text-[#050505] border-[#E9E9E9] border-2 bg-white"
              }`}
            >
              Fixed
            </button>
            <button
              type="button"
              onClick={() => {
                setFormData((prev) => ({
                  ...prev,
                  pricingModel: "Variant Based",
                }));
                if (onChange) {
                  onChange({ pricingModel: "Variant Based" });
                }
              }}
              className={`w-1/2 px-6 py-2.5 rounded-lg font-Urbanist text-[14px] font-medium transition-colors ${
                formData.pricingModel === "Variant Based"
                  ? "bg-[#FF8A00] text-white"
                  : isDarkMode
                    ? "text-gray-300 border-gray-600 border-2 bg-gray-700"
                    : "text-[#050505] border-[#E9E9E9] border-2 bg-white"
              }`}
            >
              Variant Based
            </button>
          </div>
        </div>

        {/* Base Price */}
        <div>
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            Base Price
          </label>
          <input
            type="number"
            name="basePrice"
            value={formData.basePrice}
            onChange={handleChange}
            placeholder="In Rupees"
            onWheel={(e) => e.currentTarget.blur()}
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        </div>

        {/* Compare Price */}
        <div>
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            Compare Price
          </label>
          <input
            type="number"
            name="comparePrice"
            value={formData.comparePrice}
            onChange={handleChange}
            placeholder="In Rupees"
            onWheel={(e) => e.currentTarget.blur()}
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        </div>

        {/* GST Inclusive */}
        <div className="flex items-center justify-between">
          <label className={`font-Urbanist text-[15px] font-medium leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            GST Inclusive?
          </label>
          <button
            type="button"
            onClick={handleToggle}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formData.gstInclusive ? "bg-[#FF8A00]" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.gstInclusive ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {/* GST Rate */}
        <div>
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            GST Rate
          </label>
          <select
            name="gstRate"
            value={formData.gstRate}
            onChange={handleChange}
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors appearance-none ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: "right 0.5rem center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "1.5em 1.5em",
              paddingRight: "2.5rem",
            }}
          >
            <option value="">Select GST Rate</option>
            {gstRateOptions.map((rate) => (
              <option key={rate} value={rate}>
                {rate}
              </option>
            ))}
          </select>
        </div>

        {/* Packaging Charge */}
        <div>
          <label className={`block font-Urbanist text-[15px] font-medium mb-2 leading-[140%] ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}>
            Packaging Charge
          </label>
          <input
            type="number"
            name="packagingCharge"
            value={formData.packagingCharge}
            onChange={handleChange}
            placeholder="In Rupees"
            className={`w-full px-4 py-3 border rounded-lg font-Urbanist text-[14px] focus:outline-none focus:border-[#FF8A00] transition-colors ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="text-red-500 text-sm font-Urbanist">{error}</div>
        )}

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
          <button
            type="button"
            onClick={onPrevious}
            className={`w-full sm:flex-1 border-2 hover:border-gray-400 font-Urbanist text-sm sm:text-[16px] font-semibold py-3 sm:py-3.5 rounded-full transition-colors ${
              isDarkMode
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-[#050505]'
            }`}
          >
            Previous
          </button>
          <button
            type="submit"
            className="w-full sm:flex-1 bg-[#FF8A00] hover:bg-[#E67A00] text-white font-Urbanist text-sm sm:text-[16px] font-semibold py-3 sm:py-3.5 rounded-full transition-colors"
          >
            Next
          </button>
        </div>
      </form>
    </div>
  );
};

export default DishForm3;
